<template>
  <div class="parking99-gate-control">
    <el-alert
      title="现场安全提示"
      type="warning"
      :closable="false"
      show-icon
      description="远程控制会作用于真实闸机。操作前必须确认车道无人、无车；系统每次只向页面所示真实通道ID下发一次命令，不自动重试，也不会自动联动其他通道。"
    />

    <el-card v-if="!loading && configs.length === 0" class="empty-card">
      <div class="empty-text">当前账号没有可访问的9.9车道配置。</div>
    </el-card>

    <el-card
      v-for="config in configs"
      :key="config.configId"
      class="config-card"
      shadow="never"
    >
      <div slot="header" class="card-header">
        <div>
          <span class="community-name">{{ config.communityName }}</span>
          <el-tag
            size="mini"
            :type="config.enabled === 'Y' ? 'success' : 'danger'"
          >
            {{ config.enabled === 'Y' ? '车场配置已启用' : '车场配置已停用' }}
          </el-tag>
          <span class="sync-time">最近同步：{{ config.lastSyncTime || '尚未同步' }}</span>
        </div>
        <el-button
          size="small"
          icon="el-icon-refresh"
          :loading="refreshingConfigId === config.configId"
          @click="refreshChannels(config)"
        >
          人工刷新通道
        </el-button>
      </div>

      <el-alert
        v-if="!config.integrationEnabled || !config.remoteControlEnabled"
        class="switch-alert"
        title="服务器远程控制总开关未启用，当前页面仅可查看和刷新通道。"
        type="error"
        :closable="false"
        show-icon
      />

      <el-table
        v-loading="loading"
        :data="config.channels"
        border
        style="width: 100%"
        empty-text="尚未同步到通道，请点击“人工刷新通道”"
      >
        <el-table-column prop="channelId" label="真实通道ID" width="110" align="center">
          <template slot-scope="scope">
            <strong>{{ scope.row.channelId }}</strong>
          </template>
        </el-table-column>
        <el-table-column prop="channelName" label="通道名称" min-width="180" />
        <el-table-column prop="channelTypeName" label="类型" width="80" align="center" />
        <el-table-column label="设备状态" width="100" align="center">
          <template slot-scope="scope">
            {{ scope.row.deviceStatus === null || scope.row.deviceStatus === undefined
              ? '--' : scope.row.deviceStatus }}
          </template>
        </el-table-column>
        <el-table-column label="现场关系与风险" min-width="320">
          <template slot-scope="scope">
            <div v-if="scope.row.associatedChannels && scope.row.associatedChannels.length">
              <el-tag
                v-for="associated in scope.row.associatedChannels"
                :key="associated"
                type="warning"
                size="mini"
                class="relation-tag"
              >
                关联：{{ associated }}
              </el-tag>
            </div>
            <div v-if="scope.row.warning" class="warning-text">
              {{ scope.row.warning }}
            </div>
            <span
              v-if="!scope.row.warning
                && (!scope.row.associatedChannels || !scope.row.associatedChannels.length)"
            >
              --
            </span>
          </template>
        </el-table-column>
        <el-table-column label="远程控制" min-width="210">
          <template slot-scope="scope">
            <el-alert
              v-if="scope.row.disabledReason"
              :title="scope.row.disabledReason"
              type="error"
              :closable="false"
              show-icon
            />
            <div v-else>
              <el-button
                size="mini"
                type="success"
                :loading="operatingKey === operationKey(config, scope.row, 'OPEN')"
                :disabled="Boolean(operatingKey)"
                @click="confirmOperate(config, scope.row, 'OPEN')"
              >
                抬杆
              </el-button>
              <el-button
                size="mini"
                type="warning"
                :loading="operatingKey === operationKey(config, scope.row, 'CLOSE')"
                :disabled="Boolean(operatingKey)"
                @click="confirmOperate(config, scope.row, 'CLOSE')"
              >
                落杆
              </el-button>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="出口缴费码" min-width="170" align="center">
          <template slot-scope="scope">
            <span v-if="scope.row.channelType !== 2">--</span>
            <el-button
              v-else-if="scope.row.payCodeReady"
              size="mini"
              type="primary"
              icon="el-icon-download"
              :loading="payCodeKey === codeKey(config, scope.row)"
              :disabled="Boolean(payCodeKey)"
              @click="downloadPayCode(config, scope.row)"
            >
              下载二维码
            </el-button>
            <el-button
              v-else
              size="mini"
              type="success"
              :loading="payCodeKey === codeKey(config, scope.row)"
              :disabled="Boolean(payCodeKey)"
              @click="confirmGeneratePayCode(config, scope.row)"
            >
              {{ scope.row.payCodeState === 'FAILED'
                ? '重试生成缴费码' : '生成缴费码' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import {
  listParking99Channels,
  refreshParking99Channels,
  operateParking99Gate,
  generateParking99ExitPayCode,
  downloadParking99ExitPayCode
} from '@/api/car/parking99GateControlApi'

export default {
  name: 'Parking99GateControlList',
  data() {
    return {
      loading: false,
      configs: [],
      refreshingConfigId: '',
      operatingKey: '',
      payCodeKey: ''
    }
  },
  created() {
    this.loadChannels()
  },
  methods: {
    async loadChannels() {
      this.loading = true
      try {
        const result = await listParking99Channels()
        this.configs = result.data || []
      } catch (error) {
        this.$message.error(this.errorMessage(error, '获取车道列表失败'))
      } finally {
        this.loading = false
      }
    },
    async refreshChannels(config) {
      this.refreshingConfigId = config.configId
      try {
        const result = await refreshParking99Channels({
          configId: config.configId,
          communityId: config.communityId
        })
        const refreshed = result.data
        const index = this.configs.findIndex(item => item.configId === config.configId)
        if (index !== -1) {
          this.$set(this.configs, index, refreshed)
        }
        this.$message.success('真实通道已刷新')
      } catch (error) {
        this.$message.error(this.errorMessage(error, '刷新真实通道失败'))
      } finally {
        this.refreshingConfigId = ''
      }
    },
    operationKey(config, channel, action) {
      return `${config.configId}:${channel.channelId}:${action}`
    },
    codeKey(config, channel) {
      return `${config.configId}:${channel.channelId}`
    },
    async confirmGeneratePayCode(config, channel) {
      const retry = channel.payCodeState === 'FAILED'
      const message = retry
        ? `将使用该出口首次创建时已经永久绑定的短 token，重试生成“${config.communityName}-${channel.channelName}”缴费码图片；不会更换 token。`
        : `将首次生成“${config.communityName}-${channel.channelName}”出口缴费码。生成后会永久绑定当前停车场和真实出口通道，不能更换、重置或删除。`
      try {
        await this.$confirm(message, '永久绑定二次确认', {
          confirmButtonText: retry ? '确认使用原 token 重试' : '确认永久生成',
          cancelButtonText: '取消',
          type: 'warning',
          closeOnClickModal: false,
          closeOnPressEscape: false
        })
      } catch (cancelled) {
        return
      }
      const key = this.codeKey(config, channel)
      this.payCodeKey = key
      try {
        await generateParking99ExitPayCode({
          configId: config.configId,
          communityId: config.communityId,
          channelId: channel.channelId
        })
        await this.loadChannels()
        this.$message.success('出口缴费码已永久生成，可下载')
      } catch (error) {
        this.$message.error(this.errorMessage(error, '生成出口缴费码失败'))
      } finally {
        this.payCodeKey = ''
      }
    },
    async downloadPayCode(config, channel) {
      const key = this.codeKey(config, channel)
      this.payCodeKey = key
      try {
        const result = await downloadParking99ExitPayCode({
          configId: config.configId,
          communityId: config.communityId,
          channelId: channel.channelId
        })
        const file = result.data || {}
        const binary = window.atob(file.base64 || '')
        const bytes = new Uint8Array(binary.length)
        for (let index = 0; index < binary.length; index += 1) {
          bytes[index] = binary.charCodeAt(index)
        }
        const url = window.URL.createObjectURL(new Blob(
          [bytes], { type: file.contentType || 'image/png' }
        ))
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = file.fileName || '出口缴费码.png'
        document.body.appendChild(anchor)
        anchor.click()
        document.body.removeChild(anchor)
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.$message.error(this.errorMessage(error, '下载出口缴费码失败'))
      } finally {
        this.payCodeKey = ''
      }
    },
    async confirmOperate(config, channel, action) {
      const actionName = action === 'OPEN' ? '抬杆' : '落杆'
      const associations = channel.associatedChannels && channel.associatedChannels.length
        ? ` 关联通道：${channel.associatedChannels.join('、')}。`
        : ''
      const risk = channel.warning ? ` ${channel.warning}` : ''
      const message = `即将向真实通道ID ${channel.channelId}（${channel.channelName}）下发${actionName}命令。`
        + `${risk}${associations} 本次仅请求当前通道一次，不自动重试。请确认现场无人、无车后再继续。`
      try {
        await this.$confirm(message, '现场安全二次确认', {
          confirmButtonText: `确认下发${actionName}`,
          cancelButtonText: '取消',
          type: 'warning',
          closeOnClickModal: false,
          closeOnPressEscape: false
        })
      } catch (cancelled) {
        return
      }

      const key = this.operationKey(config, channel, action)
      this.operatingKey = key
      try {
        const result = await operateParking99Gate({
          configId: config.configId,
          communityId: config.communityId,
          channelId: channel.channelId,
          action
        })
        const submitted = result.data && result.data.message
        if (submitted !== '命令已下发') {
          throw new Error('停车平台未返回明确的命令下发结果')
        }
        this.$message.success('命令已下发')
      } catch (error) {
        this.$message.error(this.errorMessage(error, `${actionName}命令下发失败`))
      } finally {
        this.operatingKey = ''
      }
    },
    errorMessage(error, fallback) {
      if (error && error.response && error.response.data && error.response.data.msg) {
        return error.response.data.msg
      }
      return (error && error.message) || fallback
    }
  }
}
</script>

<style scoped>
.parking99-gate-control {
  padding: 20px;
}

.empty-card,
.config-card {
  margin-top: 20px;
}

.empty-text {
  padding: 24px;
  color: #909399;
  text-align: center;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.community-name {
  margin-right: 12px;
  font-size: 16px;
  font-weight: 600;
}

.sync-time {
  margin-left: 16px;
  color: #909399;
  font-size: 13px;
}

.switch-alert {
  margin-bottom: 16px;
}

.relation-tag {
  margin: 0 6px 6px 0;
}

.warning-text {
  color: #e6a23c;
  line-height: 1.6;
}
</style>
